import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, UserPlus, Upload, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GuestTable } from "@/components/checkin/GuestTable";
import { GuestFormModal } from "@/components/checkin/GuestFormModal";
import { ImportGuestsModal } from "@/components/checkin/ImportGuestsModal";
import { AttendanceLog } from "@/components/checkin/AttendanceLog";
import { AttendanceStats } from "@/components/checkin/AttendanceStats";
import { GUEST_TYPES } from "@/components/checkin/GuestFormModal";

const FILTERS = [["", "All"], ["NOT_ARRIVED", "Not Arrived"], ["CHECKED_IN", "Checked In"]];

async function downloadExport(fmt) {
  try {
    const res = await api.get(`/guests/export?fmt=${fmt}`, { responseType: "blob" });
    const name = /filename="(.+)"/.exec(res.headers["content-disposition"] || "")?.[1] || `guests.${fmt}`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  } catch (err) { toast.error(formatApiError(err)); }
}

function Pager({ page, pages, total, setPage }) {
  return (
    <div className="flex items-center justify-between p-3 border-t border-slate-800 text-xs text-slate-400">
      <span data-testid="guest-total-count">{total} {total === 1 ? "guest" : "guests"}</span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="bg-slate-900 border-slate-700" data-testid="page-prev-btn"><ChevronLeft size={14} /></Button>
        <span data-testid="page-indicator">Page {page} of {pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} className="bg-slate-900 border-slate-700" data-testid="page-next-btn"><ChevronRight size={14} /></Button>
      </div>
    </div>
  );
}

export default function CheckIn() {
  const { effectiveRole } = useAuth();
  const isAdmin = effectiveRole === "admin";
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [gtype, setGtype] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ open: false, guest: null });
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: 25 });
    if (q.trim()) p.set("q", q.trim());
    if (status) p.set("status", status);
    if (gtype) p.set("guest_type", gtype);
    api.get(`/guests?${p}`).then((r) => setData(r.data)).catch(() => {});
    api.get("/guests/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/guests/logs?limit=30").then((r) => setLogs(r.data)).catch(() => {});
  }, [q, status, gtype, page]);

  useEffect(() => { const t = setTimeout(load, 250); const i = setInterval(load, 4000); return () => { clearTimeout(t); clearInterval(i); }; }, [load]);
  useEffect(() => { setPage(1); }, [q, status, gtype]);

  const act = async (g, path, msg) => {
    setBusyId(g.id);
    try { await api.post(`/guests/${g.id}/${path}`); toast.success(`${g.full_name} ${msg}`); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); load(); }
  };
  const onDelete = async (g) => {
    if (!window.confirm(`Delete ${g.full_name}?`)) return;
    try { await api.delete(`/guests/${g.id}`); toast.success("Guest deleted"); } catch (err) { toast.error(formatApiError(err)); }
    load();
  };

  return (
    <div className="space-y-6" data-testid="checkin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Registration</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">GUEST CHECK-IN</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setForm({ open: true, guest: null })} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-guest-btn"><UserPlus size={16} className="mr-1" /> Add Guest</Button>
          <Button onClick={() => setImportOpen(true)} variant="outline" className="bg-slate-900 border-slate-700" data-testid="import-guests-btn"><Upload size={16} className="mr-1" /> Import</Button>
          <Button onClick={() => downloadExport("xlsx")} variant="outline" className="bg-slate-900 border-slate-700" data-testid="export-xlsx-btn"><Download size={16} className="mr-1" /> Excel</Button>
          <Button onClick={() => downloadExport("csv")} variant="outline" className="bg-slate-900 border-slate-700" data-testid="export-csv-btn"><Download size={16} className="mr-1" /> CSV</Button>
        </div>
      </div>

      <AttendanceStats stats={stats} prefix="checkin-stat" />

      <div className="grid xl:grid-cols-[1fr_320px] gap-6">
        <div className="rounded-2xl border border-slate-800 bg-[#131D33] overflow-hidden">
          <div className="p-3 flex flex-col lg:flex-row gap-3 border-b border-slate-800">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone or guest ID" className="pl-9 bg-slate-900 border-slate-700 text-slate-100" data-testid="guest-search-input" />
            </div>
            <select value={gtype} onChange={(e) => setGtype(e.target.value)} className="rounded-md border border-slate-700 bg-slate-900 text-slate-100 text-xs font-bold px-3 py-2" data-testid="guest-type-filter">
              <option value="">All Ticket Types</option>
              {GUEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="flex flex-wrap gap-1">
              {FILTERS.map(([v, l]) => (
                <button key={l} onClick={() => setStatus(v)} className={`px-3 py-2 rounded-md text-xs font-bold transition-colors ${status === v ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-300 hover:bg-slate-800"}`} data-testid={`filter-${l.toLowerCase().replace(" ", "-")}`}>{l}</button>
              ))}
            </div>
          </div>
          <GuestTable items={data.items} isAdmin={isAdmin} busy={!!busyId}
            onCheckIn={(g) => act(g, "checkin", "checked in")} onUndo={(g) => act(g, "undo", "check-in undone")}
            onEdit={(g) => setForm({ open: true, guest: g })} onDelete={onDelete} />
          <Pager page={data.page || page} pages={data.pages} total={data.total} setPage={setPage} />
        </div>
        <AttendanceLog logs={logs} />
      </div>

      <GuestFormModal open={form.open} guest={form.guest} onClose={() => setForm({ open: false, guest: null })} onSaved={load} />
      <ImportGuestsModal open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
    </div>
  );
}
